import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, max } from 'rxjs';
import { HomeService } from '../../../services/home.service';
import { PrimengModule } from '../../../modules/primeng/primeng.module';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SpinnerComponent } from '../spinner/spinner.component';
import { NotificationService } from '../../../services/notification.service';

/**
 * Enum for file upload types - used to distinguish between different upload sections
 */
enum FileType {
  OPERATIONS = 'OPERATIONS',
  AIR = 'AIR',
  OA = 'OA',
  Engineering = 'Engineering'
}

/**
 * Standard file item interface for consistent data structure across file types
 */
interface FileItem {
  key: string;   // Filename for AIR/OA, name for Operations
  value: string | number; // CAI for AIR/OA, size for Operations
  recId?: string; // Record ID from the database for deletion
}

@Component({
  selector: 'app-section-details',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    PanelModule,
    ButtonModule,
    DialogModule,
    FileUploadModule,
    PrimengModule,
    ConfirmDialogModule
  ],
  providers: [DatePipe, ConfirmationService],
  templateUrl: './section-details.component.html',
  styleUrl: './section-details.component.scss'
})
export class SectionDetailsComponent implements OnInit {
  // Expose FileType enum to the template
  FileType = FileType;
  loading = true;
  // File upload related properties
  currentUploadType: FileType = FileType.OPERATIONS;
  showDialog = false;
  operationsFileData: FileItem[] = [];
  AIRFileData: FileItem[] = [];
  OAFileData: FileItem[] = [];
  EngineeringFileData: FileItem[] = [];
  selectedFile: File | null = null;
  uploadMessage = '';
  uploadStatus: 'success' | 'error' | '' = '';
  isUploading = false;
  isDeletingFile = false;
  // Facility information
  facilityId = localStorage.getItem('current_facility_id') || 'ES'; // Default facility ID, could be retrieved dynamically
  currentLoginUser = ''; // Current user's CAI, should be retrieved from auth service
  IntegrityThreats: { key: string; value: string }[] = [];
  repumpHistory: { tagNumber: string; repumpDate: string; userId: string; repumpComments: string }[] = [];
  cssClass: string = ''; // CSS class for status color
  technicalJustificationOptions = [
    { label: '', value: '0' },
    { label: 'Performing a TLR would allow for continued safe operation and prevent the LPG associated with shutting down the unit. A Permanent repair will be implemented during the next available maintenance opportunity.', value: '1' },
    { label: 'Other (Please provide Technical Justification below)', value: '2' },
  ];
  /**
   * Returns the appropriate dialog title based on the current upload type
   */
  get dialogTitle(): string {
    switch (this.currentUploadType) {
      case FileType.AIR: return 'Upload AIR Document';
      case FileType.OA: return 'Upload OA Document';
      default: return 'Upload Operations Document';
    }
  }

  display: boolean = false;
  operationsData: { key: string; value: string }[] = [];
  installationData: { key: string; value: string }[] = [];
  maximoData: { key: string; value: string }[] = [];
  KMSData: { key: string; value: string }[] = [];
  AIRData: { key: string; value: string }[] = [];
  workOrders: { key: string; value: string, color: string }[] = [];
  inspectionData: { key: string; value: string }[] = [];
  OAData: { key: string; value: string, class?: string }[] = [];
  EngineeringData: { key: string; value: string }[] = [];
  hasEngineeringData: boolean = false;
  hasOAData: boolean = false;
  hasAIRData: boolean = false;

  // Permanent Repair Date Changes data
  permanentRepairDateChanges: {
    dateChanged: string;
    cai: string;
    oldDate: string;
    newDate: string;
    justification: string
  }[] = [];

  airfileNameData: [] = [];
  operationFileName = [
    { workOrderNumber: '', status: '' },
  ];
  tagNumber: string = '';

  @ViewChild('pdfContent') pdfContentRef?: ElementRef<HTMLElement>;
  isDownloadingPdf = false;

  constructor(
    private homeService: HomeService,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private router: Router,
    private messageService: NotificationService,
    private confirmationService: ConfirmationService
  ) {
    this.currentLoginUser = sessionStorage.getItem('currentUser') || '';
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.tagNumber = params.get('tagNumber') || '';
      if (this.tagNumber) {
        this.getTagDetail(this.tagNumber);
      }
    });
  }

  /**
   * Maps TLR Type codes to their full descriptions
   * @param tlrTypeCode The single letter code (A, B, C, D, E)
   * @returns The full description with code prefix
   */
  private getTLRTypeDescription(tlrTypeCode: string): string {
    const tlrTypeMap: { [key: string]: string } = {
      'A': 'A: Commercial pipe clamp no injection needed',
      'B': 'B: Mechanical enclosures using injectable sealant, composite wraps and wire wraps',
      'C': 'C: Drill and Inject (injection ports)',
      'D': 'D: Welded enclosures, welded patches',
      'E': 'E: Threaded Plugs (ex: Nozzle/repad weephole plug or PRD bellows plug)'
    };

    return tlrTypeMap[tlrTypeCode?.toUpperCase()] || tlrTypeCode || '';
  }

  /**
   * Maps Service Class codes to their full descriptions
   * @param serviceClassCode The numeric code (1, 2, 3, 4)
   * @returns The full description with class prefix
   */
  private getServiceClassDescription(serviceClassCode: string | number): string {
    const serviceClassMap: { [key: string]: string } = {
      '1': 'Class 1: Services with the highest potential of resulting in an immediate emergency if a leak were to occur',
      '2': 'Class 2: Services not included in other classes',
      '3': 'Class 3: Services either flammable but do not significantly vaporize when they leak(below the flash point) or flammable but are in remote areas and operate below the boiling point',
      '4': 'Class 4: Services that are nonflammable and nontoxic are in Class 4, as are most utility services'
    };

    return serviceClassMap[serviceClassCode?.toString()] || serviceClassCode?.toString() || '';
  }
  private getTECHNICALDescription(tdData: string | number): string {
    const tdClassMap: { [key: string]: string } = {
      '0': '',
      '1': 'Performing a TLR would allow for continued safe operation and prevent the LPG associated with shutting down the unit. A Permanent repair will be implemented during the next available maintenance opportunity.',
      '2': 'Other (Please provide Technical Justification below)',
    };

    return tdClassMap[tdData?.toString()] || '';
  }


  /**
   * Get the total count of repump history entries
   * @returns The total number of repump entries
   */
  getTotalRepumps(): number {
    return this.repumpHistory.length;
  }

  downloadFile(fileObject: any) {
    this.homeService.downloadFile(fileObject.recId).subscribe({
      next: (response: any) => {
        // Create a blob from the response and trigger download
        const blob = new Blob([response], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileObject.key; // Use the filename from the file object
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url); // Clean up the URL object
      }
    });
  }

  getTagDetail(tagNumber: string) {
    this.loading = true;
    this.homeService.getHomeDetailData(tagNumber).pipe(finalize(() => this.loading = false))
      .subscribe((res: any) => {
        const operationsData = res.Opeartion[0] || [];
        const installationRemovalData = res['Installation_Date'][0] || [];
        const maximoData = res.Maximo[0] || [];
        const KMSData = res['KMS/MOC'] || [];
        const AIRData = res['AIR'][0] || [];
        // const AIRFileData  = res['AIR_Files'] || [];
        const inspectionData = res['Inspections'][0] || [];
        const OAData = res['OA'][0] || [];
        const engineeringData = res['Engineering'][0] || [];
        const permanentRepairDateChanges = res['OA_PermanentRepairDates'] || [];
        const operationFormattedDate = this.datePipe.transform(operationsData.de_date_entered, 'MM/dd/yyyy hh:mm:ss a') || '';
        let status = operationsData.tlr_status || '';
        this.cssClass = '';
        if (status === 'I') {
          status = 'Installed';
          this.cssClass = 'green'
        } else if (status === 'P') {
          status = 'Pre Installation';
          this.cssClass = 'blue'
        } else if (status === 'R') {
          status = 'Removed';
          this.cssClass = 'red'
        }
        this.operationsData = [
          { key: 'Date Entered Into System', value: operationFormattedDate || '' },
          { key: 'Operator User ID	', value: operationsData.de_cai || '' },
          { key: 'TLR Status', value: status || '' },
          { key: 'Repair Type', value: operationsData.repair_type_name || '' },
          { key: 'Tag Number', value: operationsData.TagNumber || '' },
          { key: 'Division', value: operationsData.division_name || '' },
          { key: 'Section', value: operationsData.section_name || '' },
          { key: 'Plant', value: operationsData.plant_name || '' },
          { key: 'Shutdown Description', value: operationsData.shutdown_description || '' },
          { key: 'Associated Plant - Shutdown', value: operationsData.assoc_plant_shutdown || '' },
          { key: 'Rule 1173', value: operationsData.rule_1173 || '' },
          { key: 'Leaking From', value: operationsData.leaking_name || '' },
          { key: 'Other Leak Type specified here', value: operationsData.leaking_other || '' },
          { key: 'Operating PSI	', value: operationsData.operating_PSI || '' },
          { key: 'Operating Temp', value: operationsData.operating_temperature || '' },
          { key: 'Vapor/Liquid	', value: operationsData.material_state_name || '' },
          { key: 'Service', value: operationsData.service_operations || '' },
          { key: 'Other Service', value: operationsData.service_other || '' },
          { key: 'Permanent Repair Completed', value: operationsData.PermRepairComplete == "0" ? "No" : (operationsData.PermRepairComplete ? "Yes" : '') },
          { key: 'Technical Justification', value: this.getTECHNICALDescription(operationsData.TechnicalJustification) || '' },
          { key: 'Location / Comments', value: operationsData.operator_comments || '' },
        ];

        this.installationData = [
          { key: 'Installation Date', value: installationRemovalData.TLRInstallationDate ? this.datePipe.transform(installationRemovalData.TLRInstallationDate, 'M/d/yyyy') || '' : '' },
          { key: 'Removal Date', value: installationRemovalData.TLRRemovalDate ? this.datePipe.transform(installationRemovalData.TLRRemovalDate, 'M/d/yyyy') || '' : '' },
          { key: 'Modified By', value: installationRemovalData.ModifiedByCAI || '' },
          { key: 'Modified Date', value: installationRemovalData.DateModified ? this.datePipe.transform(installationRemovalData.DateModified, 'M/d/yyyy') || '' : '' },
        ];

        this.maximoData = [
          { key: '', value: 'Maximo Login' },
          { key: 'TLR Description', value: maximoData['TLR_Description'] || '' },
          { key: 'Description', value: maximoData['Description'] || '' },
          { key: 'Work Order Number', value: maximoData['Work_Order'] || '' }, { key: 'Work Order Status', value: maximoData['Work_Order_Status'] || '' },
          { key: 'Work Type', value: maximoData['Work_Type'] || '' },
          { key: 'Work Order Status Date', value: maximoData['Work_Order_Status_Date'] ? this.datePipe.transform(maximoData['Work_Order_Status_Date'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Priority/Criticality', value: maximoData['Priority_Criticality'] || '' },
          { key: 'Planner', value: maximoData['Planner'] || '' },
          { key: 'Reported By', value: maximoData['Reported_By'] || '' },
          { key: 'Report Date', value: maximoData['Report_Date'] ? this.datePipe.transform(maximoData['Report_Date'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Complete By Date', value: maximoData['Complete_by_Date'] ? this.datePipe.transform(maximoData['Complete_by_Date'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Need Date', value: maximoData['Need_date'] || '' },
          { key: 'Work Location', value: maximoData['Work_Location'] || '' },
          { key: 'Outage-TA Number', value: maximoData['Outage-T/A_Number'] || '' },
          { key: 'Asset Number', value: maximoData['Asset_Number'] || '' },
          { key: 'Legacy ID', value: maximoData['Legacy_ID'] || '' },
        ];        // Check if AIRData is empty array and show "No data available" for all fields
        if (Array.isArray(AIRData) && AIRData.length === 0) {
          this.AIRData = [
            { key: 'AIR Date', value: '' },
            { key: 'AIR User ID', value: 'No Data Available' },
            { key: 'Cost Center', value: '' },
            { key: 'LDAR Blue TagNumber', value: '' },
            { key: 'Last Inspection Date', value: '' },
            { key: 'TLR Repair Info', value: '' },
            { key: 'PNID', value: '' },
            { key: 'AIR Comments', value: '' },
          ];
          this.hasAIRData = false;
        } else {
          this.AIRData = [
            { key: 'AIR Date', value: AIRData.AIRDate || '' },
            { key: 'AIR User ID', value: AIRData.AIRCAI || '' },
            { key: 'Cost Center', value: AIRData.CostCenter || '' },
            { key: 'LDAR Blue TagNumber', value: AIRData.LDARBlue || '' },
            { key: 'Last Inspection Date', value: AIRData.LastInspectionDate || '' },
            { key: 'TLR Repair Info', value: AIRData.RepairInfo || '' },
            { key: 'PNID', value: AIRData.PNID || '' },
            { key: 'AIR Comments', value: AIRData.Comments || '' },
          ];
          this.hasAIRData = true;
        }// Check if inspectionData is empty array and show "No data available" for all fields
        if (Array.isArray(inspectionData) && inspectionData.length === 0) {
          this.inspectionData = [
            { key: 'Date Modified', value: '' },
            { key: 'Inspector User ID', value: 'No data available' },
            { key: 'Service Class', value: '' },
            { key: 'OPPIS Line?', value: '' },
            { key: 'Inspection ISO No.', value: '' },
            { key: 'EWO Required', value: '' },
            { key: 'Inspector Comments', value: '' },
          ];
        } else {
          this.inspectionData = [
            { key: 'Date Modified', value: inspectionData['inspection_date'] ? this.datePipe.transform(inspectionData['inspection_date'], 'MM/dd/yyyy hh:mm:ss a') || '' : '' },
            { key: 'Inspector User ID', value: inspectionData['inspection_cai'] || '' },
            { key: 'Service Class', value: this.getServiceClassDescription(inspectionData['ServiceClass']) },
            { key: 'OPPIS Line?', value: inspectionData['OPPISLineIndicator'] == "N" ? "No" : (inspectionData['OPPISLineIndicator'] ? "Yes" : '') },
            { key: 'Inspection ISO No.', value: inspectionData['InspectionISONo'] || '' },
            { key: 'EWO Required', value: inspectionData['no_ewo_required'] == "0" ? "No" : (inspectionData['no_ewo_required'] ? "Yes" : '') },
            { key: 'Inspector Comments', value: inspectionData['inspector_comments'] || '' },
          ];
        }
        // Check if OAData is empty array and show "No data available" for all fields
        if (Array.isArray(OAData) && OAData.length === 0) {
          this.OAData = [
            { key: 'OA Date', value: '' },
            { key: 'OA User ID', value: 'No data available' },
            { key: 'NAMO Date', value: '' },
            { key: 'Permanent Repair Date', value: '' },
            { key: 'Permanent Repair WO', value: '' },
            { key: 'MOC WO No.', value: '' },
            { key: 'TLR Surveillance', value: '' },
            { key: 'TLR Closure User ID', value: '' },
            { key: 'TLR Closure TimeStamp', value: '' },
            { key: 'OA Comments', value: '' },
          ];
          this.hasOAData = false;
        } else {
          this.OAData = [
            { key: 'OA Date', value: OAData['OADate'] || '' },
            { key: 'OA User ID', value: OAData['OACAI'] || '' },
            { key: 'NAMO Date', value: OAData['NAMODate'] || '' },
            { key: 'Permanent Repair Date', value: OAData['PermRepairDate'] || '', class: this.getDateClass(OAData['PermRepairDate']) },
            { key: 'Permanent Repair WO', value: OAData['WONUM'] || '' },//need to check this key
            { key: 'MOC WO No.', value: OAData['MOCWONUM'] || '' },
            { key: 'TLR Surveillance', value: OAData['Surveillance'] || '' },//need to check key
            { key: 'TLR Closure User ID	', value: OAData['TLRClosureCAI'] || '' },
            { key: 'TLR Closure TimeStamp', value: OAData['TLRClosureTimeStamp'] || '' },
            { key: 'OA Comments', value: OAData['Comments'] || '' },
          ];
          this.hasOAData = true;
        }// Check if engineeringData is empty array and show "No data available" for all fields
        if (Array.isArray(engineeringData) && engineeringData.length === 0) {
          this.hasEngineeringData = false;
          this.EngineeringData = [
            { key: 'Date Modified', value: '' },
            { key: 'Engineer User ID', value: 'No data available' },
            { key: 'Engineering Work Order (EWO)', value: '' },
            { key: 'EWO Completed', value: '' },
            { key: 'TLR Type', value: '' },
            { key: 'Risk Review', value: '' },
            { key: 'Engineer Comments ', value: '' },
          ];
        } else {
          this.hasEngineeringData = true;
          this.EngineeringData = [ // Need to get values from API
            { key: 'Date Modified', value: engineeringData['engineering_date'] ? this.datePipe.transform(engineeringData['engineering_date'], 'MM/dd/yyyy hh:mm:ss a') || '' : '' },
            { key: 'Engineer User ID', value: engineeringData['engineering_cai'] || '' }, { key: 'Engineering Work Order (EWO)', value: engineeringData['engineering_work_order'] || '' },
            { key: 'EWO Completed', value: engineeringData['completed'] == '0' ? 'No' : (engineeringData['completed'] ? 'Yes' : '') },
            { key: 'TLR Type', value: this.getTLRTypeDescription(engineeringData['TLRType']) },
            { key: 'Risk Review', value: engineeringData['RiskReview']?.toString() === 'false' ? 'No' : (engineeringData['RiskReview'] ? 'Yes' : '') },
            { key: 'Engineer Comments ', value: engineeringData['engineering_comments'] || '' },
          ];
        }

        this.workOrders = res.Maximo.map((m: { [key: string]: string }) => ({
          key: m['Work_Order'] || '',
          value: m['Work_Order_Status'] || '',
          color: 'btn-success'
        }));

        this.workOrders[0] = { ...this.workOrders[0], color: "btn-warning" }

        // Standardize the structure for Operation files from API including RecID
        if (res['Operations_Files'] && Array.isArray(res['Operations_Files'])) {
          this.operationsFileData = res['Operations_Files']
            .filter((m: { [key: string]: string }) => m['Filename']?.trim() !== '')
            .map((m: { [key: string]: string }) => ({
              key: m['Filename'] || '',
              value: m['CAI'] || '',
              recId: m['RecID'] || ''
            }));
        } else {
          this.operationsFileData = [];
        }


        // Standardize the structure for AIR files from API including RecID
        if (res['AIR_Files'] && Array.isArray(res['AIR_Files'])) {
          this.AIRFileData = res['AIR_Files']
            .filter((m: { [key: string]: string }) => m['Filename']?.trim() !== '')
            .map((m: { [key: string]: string }) => ({
              key: m['Filename'] || '',
              value: m['CAI'] || '',
              recId: m['RecID'] || ''
            }));
        } else {
          this.AIRFileData = [];
        }

        // Standardize the structure for OA files from API including RecID
        if (res['OA_Files'] && Array.isArray(res['OA_Files'])) {
          this.OAFileData = res['OA_Files']
            .filter((m: { [key: string]: string }) => m['Filename']?.trim() !== '')
            .map((m: { [key: string]: string }) => ({
              key: m['Filename'] || '',
              value: m['CAI'] || '',
              recId: m['RecID'] || ''
            }));
        } else {
          this.OAFileData = [];
        }

        if (res['Engineering_Files'] && Array.isArray(res['Engineering_Files'])) {
          this.EngineeringFileData = res['Engineering_Files']
            .filter((m: { [key: string]: string }) => m['Filename']?.trim() !== '')
            .map((m: { [key: string]: string }) => ({
              key: m['Filename'] || '',
              value: m['CAI'] || '',
              recId: m['RecID'] || ''
            }));
        } else {
          this.EngineeringFileData = [];
        }

        // Check if KMSData is empty array and show "No data available" for all fields
        if (Array.isArray(KMSData) && KMSData.length === 0) {
          this.KMSData = [
            { key: '', value: 'KMS/MOC Login' },
            { key: 'Ref. No.', value: 'No data available' },
            { key: 'MOC Status', value: '' },
            { key: 'MOC Owner', value: '' },
            { key: 'Creation Date', value: '' },
            { key: 'Source Type', value: '' },
            { key: 'Basis', value: '' },
            { key: 'Target Date', value: '' },
            { key: 'Closure Date', value: '' },
            { key: 'MOC Title', value: '' },
            { key: 'MOC Description', value: '' },
            { key: 'MOC Justify', value: '' },
            { key: 'Closure Comments', value: '' },
          ];
        } else {
          this.KMSData = [
            { key: '', value: 'KMS/MOC Login' },
            { key: 'Ref. No.', value: KMSData['Ref No'] || '' },
            { key: 'MOC Status', value: KMSData['MOC Status'] || '' },
            { key: 'MOC Owner', value: KMSData['MOC Owner'] || '' },
            { key: 'Creation Date', value: KMSData['Creation Date'] || '' },
            { key: 'Source Type	', value: KMSData['Source Type'] || '' },
            { key: 'Basis', value: KMSData['Basis'] || '' },
            { key: 'Target Date', value: KMSData['Target Date'] || '' },
            { key: 'Closure Date', value: KMSData['Closure Date'] || '' },
            { key: 'MOC Title', value: KMSData['MOC Title'] || '' },
            { key: 'MOC Description', value: KMSData['MOC Description'] || '' },
            { key: 'MOC Justify', value: KMSData['MOC Justify'] || '' },
            { key: 'Closure Comments', value: KMSData['Closure Comments'] || '' },
          ];
        }        // Check if IntegrityThreats is empty array and show "No data available" for all fields
        const integrityThreatsData = res['Integrity_Threats'] || [];
        if (Array.isArray(integrityThreatsData) && integrityThreatsData.length === 0) {
          this.IntegrityThreats = [
            { key: 'Inspection Recommendation ID', value: '' },
            { key: 'Recommendation Type', value: 'No data available' },
            { key: 'Recommendation Category', value: '' },
            { key: 'Recommendation Priority', value: '' },
            { key: 'Recommendation Headline', value: '' },
            { key: 'Equipment ID', value: '' },
            { key: 'Equipment Brass Tag', value: '' },
            { key: 'Functional Location ID', value: '' },
            { key: 'Status', value: '' },
            { key: 'Author Name', value: '' },
            { key: 'Reviewer Name', value: '' },
            { key: 'Final Approver Name', value: '' },
            { key: 'Target Completion Date', value: '' },
            { key: 'Work Order Number', value: '' }
          ];
        } else {
          this.IntegrityThreats = [ // these are assumption values need to update with actual values once we get data from api
            { key: 'Inspection Recommendation ID', value: integrityThreatsData[0]?.['Mer_Rec_ID'] || '' },
            { key: 'Recommendation Type', value: integrityThreatsData[0]?.['Recommendation_Type'] || '' },
            { key: 'Recommendation Category', value: integrityThreatsData[0]?.['Recommendation_Category'] || '' },
            { key: 'Recommendation Priority', value: integrityThreatsData[0]?.['Recommendation_Priority'] || '' },
            { key: 'Recommendation Headline', value: integrityThreatsData[0]?.['Recommendation_Headline'] || '' },
            { key: 'Equipment ID', value: integrityThreatsData[0]?.['Equipment_ID'] || '' },
            { key: 'Equipment Brass Tag', value: integrityThreatsData[0]?.['Equipment_Brass_Tag'] || '' },
            { key: 'Functional Location ID', value: integrityThreatsData[0]?.['Functional_Location_ID'] || '' },
            { key: 'Status', value: integrityThreatsData[0]?.['Status'] || '' },
            { key: 'Author Name', value: integrityThreatsData[0]?.['Author_Name'] || '' },
            { key: 'Reviewer Name', value: integrityThreatsData[0]?.['Reviewer_Name'] || '' },
            { key: 'Final Approver Name', value: integrityThreatsData[0]?.['Final_Approver_Name'] || '' },
            { key: 'Target Completion Date', value: integrityThreatsData[0]?.['Target_Completion_Date'] || '' },
            { key: 'Work Order Number', value: integrityThreatsData[0]?.['Work_Order_Number'] || '' }
          ];
        }
        // Handle Repump History data
        const repumpHistoryData = res['Repump_History'] || [];
        if (Array.isArray(repumpHistoryData) && repumpHistoryData.length === 0) {
          this.repumpHistory = [];
        } else {
          this.repumpHistory = repumpHistoryData.map((item: any) => ({
            tagNumber: item['Tag_Number'] || '',
            repumpDate: item['Repump_Date'] || '',
            userId: item['CAI'] || '',
            repumpComments: item['Repump_Comments'] || ''
          }));
        }

        // Handle Permanent Repair Date Changes data

        if (Array.isArray(permanentRepairDateChanges) && permanentRepairDateChanges.length > 0) {
          this.permanentRepairDateChanges = permanentRepairDateChanges.map((item: any) => ({
            dateChanged: item['Date_Changed'] ? this.datePipe.transform(item['Date_Changed'], 'M/d/yyyy') || '' : '',
            cai: item['CAI'] || '',
            oldDate: item['Old_date'] ? this.datePipe.transform(item['Old_date'], 'M/d/yyyy') || '' : '',
            newDate: item['New_Date'] ? this.datePipe.transform(item['New_Date'], 'M/d/yyyy') || '' : '',
            justification: item['Justification'] || ''
          }));
        } else {
          this.permanentRepairDateChanges = [];
        }


        this.loading = false;
      });
  }

  onFileSelected(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      this.selectedFile = fileInput.files[0];
    }
  }




  /**
   * Opens the upload dialog with the specified file type
   * Single entry point for all file type uploads
   * @param fileType The type of file being uploaded (OPERATIONS, AIR, OA)
   */
  openUploadDialog(fileType: string) {
    // Convert string to enum value
    this.currentUploadType = fileType as FileType;
    this.selectedFile = null; // Reset selected file
    this.uploadMessage = '';
    this.uploadStatus = '';
    this.showDialog = true;
  }

  /**
   * Close the upload dialog and reset state
   */
  closeDialog() {
    this.uploadMessage = '';
    this.uploadStatus = '';
    this.showDialog = false;
  }

  /**
   * Show confirmation dialog and delete file if confirmed
   * @param fileType The type of file (OPERATIONS, AIR, OA)
   * @param file The file item to delete with RecID
   * @param index The index of the file in its respective array
   */
  confirmDeleteFile(fileType: string, file: FileItem, index: number) {
    // Skip confirmation if no RecID is available (local files only)
    if (!file.recId || file.recId === '0') {
      this.deleteFileFromList(fileType, index);
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete the file "${file.key}"?`,
      header: 'Confirm File Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteFileWithApi(fileType, file, index);
      }
    });
  }

  /**
   * Delete file from server using API
   * @param fileType The type of file (OPERATIONS, AIR, OA)
   * @param file The file item containing recId
   * @param index The index of the file in its respective array
   */
  deleteFileWithApi(fileType: string, file: FileItem, index: number) {
    if (!file.recId) return;

    this.isDeletingFile = true;
    this.homeService.deleteFile(file.recId).subscribe({
      next: (response) => {
        // Remove from local list after successful deletion
        this.deleteFileFromList(fileType, index);
        this.isDeletingFile = false;
        this.messageService.showSuccess(`${file.key} was successfully deleted.`)
      },
      error: (error) => {
        this.isDeletingFile = false;
        this.messageService.showError(`Could not delete ${file.key}: ${error.message || 'Unknown error'}`)
      }
    });
  }

  /**
   * Remove file from local list
   * @param fileType The type of file (OPERATIONS, AIR, OA)
   * @param index The index of the file in its respective array
   */
  deleteFileFromList(fileType: string, index: number) {
    switch (fileType) {
      case FileType.AIR:
        this.AIRFileData.splice(index, 1);
        break;
      case FileType.OA:
        this.OAFileData.splice(index, 1);
        break;
      case FileType.Engineering:
        this.EngineeringFileData.splice(index, 1);
        break;
      default:
        this.operationsFileData.splice(index, 1);
        break;
    }
  }

  /**
   * Map file type to fileArea parameter for API
   * Operations type gets converted to 'OP' in the service layer
   * @param fileType The type of file (OPERATIONS, AIR, OA)
   * @returns The file area string for the API
   */
  getFileArea(fileType: FileType): string {
    switch (fileType) {
      case FileType.AIR: return 'AIR';
      case FileType.OA: return 'OA';
      default: return 'Operations'; // This will be converted to 'OP' in the service
    }
  }
  uploadFile() {
    if (!this.selectedFile) {
      return;
    }

    const fileName = this.selectedFile.name?.trim();
    const fileSize = Math.round(this.selectedFile.size / 1024); // Convert size to KB

    // Check for existing files to prevent duplicates
    let fileExists = false;
    switch (this.currentUploadType) {
      case FileType.AIR:
        fileExists = this.AIRFileData.some((file) => file.key === fileName);
        break;
      case FileType.OA:
        fileExists = this.OAFileData.some((file) => file.key === fileName);
        break;
      case FileType.Engineering:
        fileExists = this.EngineeringFileData.some((file) => file.key === fileName);
        break;
      default:
        fileExists = this.operationsFileData.some((file) => file.key === fileName);
        break;
    }

    // Set uploading state
    this.isUploading = true;

    // Get the appropriate file area code
    let fileAreaCode;
    switch (this.currentUploadType) {
      case FileType.AIR:
        fileAreaCode = 'AI';
        break;
      case FileType.OA:
        fileAreaCode = 'OA';
        break;
      case FileType.Engineering:
        fileAreaCode = 'EN';
        break;
      default:
        fileAreaCode = 'OP'; // Operations code
        break;
    }

    // Create dynamic payload using current values
    const dynamicPayload = {
      FacilityId: this.facilityId,
      TagNumber: this.tagNumber,
      CAI: this.currentLoginUser,
      Email: this.currentLoginUser,
      FileName: fileName,
      FileArea: fileAreaCode
    };

    const formData = new FormData();
    for (const key in dynamicPayload) {
      formData.append(key, (dynamicPayload as any)[key]);
    }
    formData.append('File', this.selectedFile);

    this.homeService.uploadFileNewData(formData).subscribe({
      next: (response) => {
        // Handle successful upload - now using the new response format
        this.isUploading = false;
        this.uploadMessage = `${fileName} uploaded successfully`;
        this.uploadStatus = 'success';
        this.messageService.showSuccess(`${fileName} was successfully uploaded.`)
        this.selectedFile = null; // Clear selected file
        // Update the appropriate file list based on type
        this.getTagDetail(this.tagNumber);
        const newFileItem: FileItem = {
          key: fileName,
          value: this.currentLoginUser,
          recId: response?.recId || ''  // Add recId from the new response format
        };

        switch (this.currentUploadType) {
          case FileType.AIR:
            this.AIRFileData.push(newFileItem);
            break;
          case FileType.OA:
            this.OAFileData.push(newFileItem);
            break;
          case FileType.Engineering:
            this.EngineeringFileData.push(newFileItem);
            break;
          default:
            // For Operations files
            this.operationsFileData.push(newFileItem);
            break;
        }

        // Clear the selected file after successful upload
        this.selectedFile = null;

        // Auto-close dialog after successful upload (optional)
        setTimeout(() => this.closeDialog(), 1500);
      },
      error: (error) => {
        // Handle upload error
        this.isUploading = false;

        let errorMessage = 'Failed to upload file';
        if (error.message) {
          errorMessage = error.message;
        }

        // Check if the error is specifically an HTTP parsing error
        if (error.name === 'HttpErrorResponse' && error.error instanceof Error) {
          if (error.error.message.includes('parsing')) {
            errorMessage = 'The server returned a response that could not be parsed. The file may have been uploaded successfully.';
          }
        }

        this.uploadMessage = `Failed to upload ${fileName}: ${errorMessage}`;
        this.uploadStatus = 'error';
        this.messageService.showError(`Error uploading ${fileName}: ${errorMessage}`);
        // Show toast message for error
      }
    });
  }

  getDateClass(date: Date): string {
    date = new Date(date); // Convert to Date object if it's a string
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for comparison

    if (date < today) {
      return 'past-date';
    } else {
      return 'future-date';
    }
  }
  /**
   * Opens the Maximo login page in a new tab
   */
  redirectToMaximo() {
    window.open('https://maxglop.manage.prod.masglobal.azure.chevron.com/maximo/ui/login', '_blank');
  }

  /**
   * Opens the KMS/MOC login page in a new tab
   */
  redirectToKMS() {
    window.open('http://kmsnet2.chevron.com/Kms/Common/switchboard.aspx', '_blank');
  }

  goToApprovals(event: MouseEvent) {
    event.preventDefault();
    this.router.navigate(['/approval-details', this.tagNumber]);
  }
  workOrderClick(btnData: any) {
    let workOrderNumber = btnData.key;
    this.homeService.getWorkOrderData(workOrderNumber).subscribe((res: any) => {
      if (res) {
        this.maximoData = [
          { key: '', value: 'Maximo Login' },
          { key: 'TLR Description', value: res['tlrDescription'] || '' },
          { key: 'Description', value: res['description'] || '' },
          { key: 'Work Order Number', value: res['workOrder'] || '' }, { key: 'Work Order Status', value: res['workOrderStatus'] || '' },
          { key: 'Work Type', value: res['workType'] || '' },
          { key: 'Work Order Status Date', value: res['workOrderStatusDate'] ? this.datePipe.transform(res['workOrderStatusDate'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Priority/Criticality', value: res['Priority_Criticality'] || '' },
          { key: 'Planner', value: res['planner'] || '' },
          { key: 'Reported By', value: res['reportedBy'] || '' },
          { key: 'Report Date', value: res['reportDate'] ? this.datePipe.transform(res['reportDate'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Complete By Date', value: res['completedByDate'] ? this.datePipe.transform(res['completedByDate'], 'M/d/yyyy h:mm:ss a') : '' },
          { key: 'Need Date', value: res['needDate'] || '' },
          { key: 'Work Location', value: res['workLocation'] || '' },
          { key: 'Outage-TA Number', value: res['outageTANumber'] || '' },
          { key: 'Asset Number', value: res['assetNumber'] || '' },
          { key: 'Legacy ID', value: res['legacyID'] || '' },
        ];
      }
    });
    this.workOrders = this.workOrders.map((wo) => {
      if (wo.key === workOrderNumber) {
        return { ...wo, color: 'btn-warning' }; // Highlight the selected work order
      }
      return { ...wo, color: 'btn-success' }; // Reset others to default color
    });
  }

  async downloadDetailsPdf() {
    if (!this.pdfContentRef?.nativeElement) return;

    this.isDownloadingPdf = true;
    let temp: HTMLDivElement | null = null;

    try {
      const html2pdfModule: any = await import('html2pdf.js');
      const html2pdf = html2pdfModule.default || html2pdfModule;

      const source = this.pdfContentRef.nativeElement;
      const clone = source.cloneNode(true) as HTMLElement;

      temp = document.createElement('div');
      temp.style.position = 'fixed';
      temp.style.left = '-100000px';
      temp.style.top = '0';
      temp.style.width = `${source.scrollWidth}px`;
      temp.style.background = '#fff';
      temp.appendChild(clone);
      document.body.appendChild(temp);

      const options = {
        margin: [6, 6, 6, 6],
        filename: `TLR_${this.tagNumber || 'Details'}.pdf`,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: {
          scale: 3,
          useCORS: true,
          backgroundColor: '#ffffff',
          scrollY: 0,
          windowWidth: source.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: {
          mode: ['css', 'legacy'],
          avoid: ['tr', 'td', 'th', '.p-table', '.p-panel', '.pdf-no-break']
        }
      };

      await html2pdf().set(options).from(clone).save();
    } catch {
      this.messageService.showError('Failed to generate PDF');
    } finally {
      if (temp && document.body.contains(temp)) {
        document.body.removeChild(temp);
      }
      this.isDownloadingPdf = false;
    }
  }
}